import { JSX, KeyboardEvent, ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { Box, ClickAwayListener, Collapse, Fade, Paper, Popper, SxProps, Theme, Tooltip } from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useSidebarState, useStore } from 'react-admin';
import { sectionStoreKey } from './menuConfig';
import { MenuSurfaceProvider } from './menuSurface';
import { liveColour } from '../../styles/theme/hqTheme';

export interface CollapsibleMenuItemProps {
    /** Section label, also the key the open/closed state is stored under. */
    title: string;
    icon?: ReactNode;
    children?: ReactNode;
    /** Expand regardless of stored state, without overwriting it (used while filtering). */
    forceOpen?: boolean;
    /** Opens the section when the current route moves inside it. */
    containsActiveRoute?: boolean;
}

/** How long the pointer may be off both the rail button and its flyout before it closes. */
const FLYOUT_GRACE_MS: number = 140;

const containerStyles: SxProps<Theme> = {
    // <Menu> is a styled MenuList, i.e. a <ul>, so its children must be <li>.
    // display:block keeps the default list-item layout out of the way.
    display: 'block',
    width: '100%',
};

/**
 * The section header is a row, not a heading: the same height as the items
 * under it, so an open section reads as one column of equal rows rather than a
 * title with a list stuck to it.
 */
const headerStyles: (marked: boolean) => SxProps<Theme> = (marked: boolean): SxProps<Theme> =>
    (theme: Theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        height: 36,
        margin: '2px 8px 2px 0',
        // Borrowed from RaMenuItemLink, deliberately: a section header and a
        // top-level link are peers, so their glyphs have to sit on one axis. The
        // 3px transparent border is what the active item fills in with yellow,
        // and a closed section holding the current screen fills it in too.
        padding: '0 10px 0 16px',
        borderLeft: `3px solid ${marked ? liveColour(theme).line : 'transparent'}`,
        borderRadius: '0 8px 8px 0',
        cursor: 'pointer',
        userSelect: 'none',
        color: theme.palette.text.secondary,
        transition: 'background-color 140ms ease, color 140ms ease',
        '&:hover': { backgroundColor: theme.palette.action.hover, color: theme.palette.text.primary },
        '&:focus-visible': {
            outline: `2px solid ${liveColour(theme).line}`,
            outlineOffset: '-2px',
        },
        '& > svg': { fontSize: 20 },
    });

/** Uppercase because it labels a group; the items below it are sentence case. */
const titleStyles: SxProps<Theme> = {
    flex: 1,
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.09em',
    textTransform: 'uppercase',
    lineHeight: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

const chevronStyles: (expanded: boolean) => SxProps<Theme> = (expanded: boolean): SxProps<Theme> => ({
    fontSize: 18,
    opacity: 0.65,
    transition: 'transform 180ms cubic-bezier(0.22, 0.61, 0.36, 1)',
    transform: expanded ? 'rotate(90deg)' : 'none',
});

/** Items sit in from the section header so the nesting is visible without a rule. */
const contentStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: '10px',
    paddingBottom: '4px',
};

/**
 * The one thing the collapsed rail could not otherwise say: this section holds
 * the screen you are on. Yellow, because that is what live means everywhere
 * else in HQ.
 */
const railMarkerStyles: SxProps<Theme> = (theme: Theme) => ({
    position: 'absolute',
    top: 6,
    right: 6,
    width: 5,
    height: 5,
    borderRadius: '50%',
    // Always drawn on the petrol tile of an active section, so the fill is safe.
    backgroundColor: liveColour(theme).fill,
});

/** The 60px rail: one centred button per section, and nothing else. */
const railButtonStyles: (active: boolean) => SxProps<Theme> = (active: boolean): SxProps<Theme> =>
    (theme: Theme) => ({
        position: 'relative',
        width: 44,
        height: 40,
        margin: '2px auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'background-color 140ms ease',
        backgroundColor: active ? theme.palette.secondary.main : 'transparent',
        color: active ? liveColour(theme).fill : theme.palette.text.secondary,
        '&:hover': {
            backgroundColor: active ? theme.palette.secondary.main : theme.palette.action.hover,
        },
        '&:focus-visible': {
            outline: `2px solid ${liveColour(theme).line}`,
            outlineOffset: '2px',
        },
        '& > svg': { fontSize: 21 },
    });

const flyoutPaperStyles: SxProps<Theme> = (theme: Theme) => ({
    minWidth: 232,
    maxWidth: 320,
    maxHeight: '72vh',
    overflowY: 'auto',
    padding: '6px 0 8px',
    borderRadius: '12px',
    border: `1px solid ${theme.palette.divider}`,
    backgroundImage: 'none',
});

const flyoutTitleStyles: SxProps<Theme> = (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '6px 14px 10px',
    margin: '0 0 4px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.09em',
    textTransform: 'uppercase',
    '& > svg': { fontSize: 18, color: theme.palette.primary.main },
});

/**
 * A collapsible group of menu items, in both of the sidebar's widths.
 *
 * Open, it is a header row and a `Collapse`. Collapsed, the header row would be
 * a mystery glyph and the `Collapse` would open a list of items 60px wide with
 * their labels cut off, which is what it used to do — so at that width the
 * section becomes a single button that opens its contents in a flyout beside
 * the rail instead. The children are the same elements either way; only where
 * they are drawn changes.
 *
 * React Admin's OSS <Menu> has no multi-level menu — <MultiLevelMenu> ships in the
 * Enterprise-only ra-navigation package — so this stays a local component, but it
 * leans on react-admin's own primitives rather than reimplementing them:
 * `useStore` persists the open/closed choice (localStorage-backed) and
 * `useSidebarState` keeps it sane when the sidebar collapses to icons.
 */
export const CollapsibleMenuItem: (props: CollapsibleMenuItemProps) => JSX.Element =
    ({ title, icon, children, forceOpen = false, containsActiveRoute = false }: CollapsibleMenuItemProps): JSX.Element => {
        // Both hooks are fully typed by their own signatures — useStore's setter also
        // accepts an updater callback, so a hand-written tuple here would be wrong.
        const [open, setOpen] = useStore<boolean>(sectionStoreKey(title), false);
        const [sidebarOpen] = useSidebarState();
        const contentId: string = useId();
        const expanded: boolean = forceOpen || open;

        const [anchor, setAnchor] = useState<HTMLElement | null>(null);
        // A plain number would be captured by the closure that scheduled it, so the
        // cancel below could be cancelling a timer that has already been replaced.
        const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

        // Reveal the section the user has navigated into. Only fires on the transition,
        // so manually closing it again while still on that route sticks.
        useEffect((): void => {
            if (containsActiveRoute) {
                setOpen(true);
            }
        }, [containsActiveRoute]);

        const cancelClose: () => void = useCallback((): void => {
            if (closeTimer.current !== null) {
                clearTimeout(closeTimer.current);
                closeTimer.current = null;
            }
        }, []);

        const scheduleClose: () => void = useCallback((): void => {
            cancelClose();
            closeTimer.current = setTimeout((): void => setAnchor(null), FLYOUT_GRACE_MS);
        }, [cancelClose]);

        // The flyout has to survive the gap between the rail button and the popper,
        // so it closes on a timer rather than on mouseleave. Clearing it on unmount
        // stops a setState landing after the sidebar has been reopened.
        useEffect((): () => void => cancelClose, [cancelClose]);

        // Reopening the sidebar while a flyout is up would leave it floating over the
        // expanded menu, which is the same content twice.
        useEffect((): void => {
            if (sidebarOpen) {
                setAnchor(null);
            }
        }, [sidebarOpen]);

        const toggle: () => void = (): void => setOpen(!open);

        const handleKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void =
            (event: KeyboardEvent<HTMLDivElement>): void => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggle();
                }
            };

        if (!sidebarOpen) {
            const flyoutOpen: boolean = anchor !== null;

            const handleRailKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void =
                (event: KeyboardEvent<HTMLDivElement>): void => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        cancelClose();
                        setAnchor(flyoutOpen ? null : event.currentTarget);
                    }
                    if (event.key === 'Escape') {
                        setAnchor(null);
                    }
                };

            return (
                <Box component="li" sx={ containerStyles }>
                    <Tooltip title={ flyoutOpen ? '' : title } placement="right">
                        <Box
                            sx={ railButtonStyles(containsActiveRoute) }
                            role="button"
                            tabIndex={ 0 }
                            aria-label={ title }
                            aria-expanded={ flyoutOpen }
                            aria-controls={ contentId }
                            onMouseEnter={ (event): void => {
                                cancelClose();
                                setAnchor(event.currentTarget);
                            } }
                            onMouseLeave={ scheduleClose }
                            onFocus={ (event): void => setAnchor(event.currentTarget) }
                            onKeyDown={ handleRailKeyDown }
                        >
                            { icon ?? null }
                            { containsActiveRoute && <Box sx={ railMarkerStyles } /> }
                        </Box>
                    </Tooltip>

                    <Popper
                        open={ flyoutOpen }
                        anchorEl={ anchor }
                        placement="right-start"
                        transition
                        modifiers={ [{ name: 'offset', options: { offset: [0, 8] } }] }
                        sx={ (theme: Theme) => ({ zIndex: theme.zIndex.drawer + 2 }) }
                    >
                        { ({ TransitionProps }): JSX.Element => (
                            <Fade { ...TransitionProps } timeout={ 120 }>
                                <Paper
                                    elevation={ 8 }
                                    id={ contentId }
                                    sx={ flyoutPaperStyles }
                                    onMouseEnter={ cancelClose }
                                    onMouseLeave={ scheduleClose }
                                >
                                    <ClickAwayListener onClickAway={ (): void => setAnchor(null) }>
                                        <Box onClick={ (): void => setAnchor(null) }>
                                            <Box sx={ flyoutTitleStyles }>
                                                { icon ?? null }
                                                <Box component="span">{ title }</Box>
                                            </Box>
                                            <MenuSurfaceProvider value="flyout">
                                                { children }
                                            </MenuSurfaceProvider>
                                        </Box>
                                    </ClickAwayListener>
                                </Paper>
                            </Fade>
                        ) }
                    </Popper>
                </Box>
            );
        }

        return (
            <Box component="li" sx={ containerStyles }>
                <Box
                    sx={ headerStyles(containsActiveRoute && !expanded) }
                    onClick={ toggle }
                    onKeyDown={ handleKeyDown }
                    role="button"
                    tabIndex={ 0 }
                    aria-expanded={ expanded }
                    aria-controls={ contentId }
                >
                    { icon ?? null }
                    <Box component="span" sx={ titleStyles }>{ title }</Box>
                    <KeyboardArrowRightIcon sx={ chevronStyles(expanded) } />
                </Box>
                <Collapse in={ expanded } id={ contentId } unmountOnExit>
                    <Box sx={ contentStyles }>
                        { children }
                    </Box>
                </Collapse>
            </Box>
        );
    };
